import { AmsPermissionClaimType, authService } from '../../../services/Auth';
import {
    ButtonGroup,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
} from 'reactstrap';
import { Institution, InstitutionPeopleDto } from '../../../common/Types';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import moment from 'moment';

import { Button } from '../../../components';
import { InstitutionPersonRole } from '../../../common/Data';
import { formatPhoneNumber } from '../../../common/Utils';
import { institutionService } from '../../../services/InstitutionService';
import { personnelService } from '../../../services/Personnel';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';

interface InstitutionPersonnelProps {
    institution: Institution;
    type: InstitutionPersonRole;
    personnel: InstitutionPeopleDto[];
}

const InstitutionPersonnel: React.FC<InstitutionPersonnelProps> = ({ institution, type, personnel }) => {
    const [rolePeople, setRolePeople] = useState<InstitutionPeopleDto[]>(() =>
        personnel.filter((r) => r.roleId === type && r.people),
    );
    const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
    const [editing, setEditing] = useState<boolean>(false);
    const [showModal, setModal] = useState<boolean>(false);

    // Create empty person template
    const createEmptyPerson = useCallback(
        (): InstitutionPeopleDto =>
            ({
                institutionId: institution.institutionId,
                institutionPeopleId: 0,
                peopleId: null, // Should be null for new person
                programId: null,
                roleId: type,
                dateCreated: new Date(),
                role: {
                    roleId: type,
                    title: InstitutionPersonRole[type],
                },
                active: true,
                people: {
                    active: true,
                    editedBy: '00000000-0000-0000-0000-000000000000',
                    createdBy: '00000000-0000-0000-0000-000000000000',
                    dateCreated: '',
                    lastEdited: '',
                    credentials: '',
                    email: '',
                    extension: '',
                    firstName: '',
                    lastName: '',
                    peopleId: 0, // This will be handled by the backend
                    phone: '',
                    salutations: '',
                    title: '',
                },
            }) as InstitutionPeopleDto,
        [institution.institutionId, type],
    );

    // Get current person based on selection
    const currentPerson = useMemo(() => {
        if (rolePeople.length === 0) {
            return createEmptyPerson();
        }

        if (selectedPersonId === null) {
            return rolePeople[0];
        }

        return rolePeople.find((rp) => rp.institutionPeopleId === selectedPersonId) || rolePeople[0];
    }, [rolePeople, selectedPersonId, createEmptyPerson]);

    // Initialize form with current person data
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
        watch,
    } = useForm<InstitutionPeopleDto>({
        defaultValues: currentPerson,
        mode: 'onChange',
    });

    // State for email lookup
    const [isLookingUp, setIsLookingUp] = useState(false);
    const [emailLookupMessage, setEmailLookupMessage] = useState<string | null>(null);

    // Watch email field to control other field states
    const emailValue = watch('people.email');
    const isEmailEntered = !!emailValue && emailValue.trim().length > 0;

    // Email lookup handler - auto-populate fields when email is entered
    const handleEmailBlur = useCallback(
        async (email: string) => {
            // Clear previous message
            setEmailLookupMessage(null);

            // Validate email format before lookup
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!email || !emailPattern.test(email)) {
                return;
            }

            setIsLookingUp(true);
            try {
                const foundPerson = await personnelService.getPersonByEmail(email);
                if (foundPerson && foundPerson.peopleId) {
                    // Populate all fields from the found person
                    setValue('peopleId', foundPerson.peopleId, { shouldDirty: true });
                    setValue('people.peopleId', foundPerson.peopleId, { shouldDirty: true });
                    setValue('people.firstName', foundPerson.firstName || '', { shouldDirty: true });
                    setValue('people.lastName', foundPerson.lastName || '', { shouldDirty: true });
                    setValue('people.title', foundPerson.title || '', { shouldDirty: true });
                    setValue('people.salutations', foundPerson.salutations || '', { shouldDirty: true });
                    setValue('people.credentials', foundPerson.credentials || '', { shouldDirty: true });
                    setValue('people.phone', foundPerson.phone || '', { shouldDirty: true });
                    setValue('people.extension', foundPerson.extension || '', { shouldDirty: true });
                    setEmailLookupMessage(`Found: ${foundPerson.firstName} ${foundPerson.lastName}`);
                }
            } catch (error) {
                // Silently fail - user can still enter data manually
                console.log('Email lookup failed:', error);
            } finally {
                setIsLookingUp(false);
            }
        },
        [setValue],
    );

    // Update form when person changes
    useEffect(() => {
        reset(currentPerson);
        setEditing(false);
    }, [currentPerson, reset]);

    // Update selected person when rolePeople changes
    useEffect(() => {
        if (rolePeople.length > 0 && selectedPersonId === null) {
            setSelectedPersonId(rolePeople[0].institutionPeopleId);
        }
    }, [rolePeople, selectedPersonId]);

    const toggleModal = useCallback(() => setModal((prev) => !prev), []);

    const reloadPersonnelFromServer = useCallback(async () => {
        if (!institution) return;

        try {
            const results = await personnelService.getAmsPersonnelByInsitutionId(institution.institutionId);
            const filtered = results.filter((r) => r.roleId === type && r.people);
            setRolePeople(filtered);
            setModal(false);

            // Select first person after reload
            if (filtered.length > 0) {
                setSelectedPersonId(filtered[0].institutionPeopleId);
            }
        } catch (error) {
            toast.error('Error loading personnel');
        }
    }, [institution, type]);

    const cancelForm = useCallback(() => {
        setEditing(false);
        reset(currentPerson);
    }, [currentPerson, reset]);

    const submitForm = useCallback(
        async (values: InstitutionPeopleDto) => {
            const toastId = toast.info('Saving person...');

            try {
                // Clean the data to prevent Entity Framework navigation property issues
                const cleanValues = {
                    ...values,
                    // Ensure roleId is set correctly
                    roleId: values.role?.roleId || values.roleId || type,
                    // Don't pass the navigation property objects to prevent EF issues
                    role: undefined,
                    people: values.people ? {
                        ...values.people,
                        // For new people, clear the peopleId to let the backend handle it
                        peopleId: values.people.peopleId === 0 ? 0 : values.people.peopleId
                    } : null
                };

                await institutionService.saveInstitutionPerson(new InstitutionPeopleDto(cleanValues));
                await reloadPersonnelFromServer();
                setEditing(false);
                toast.update(toastId, {
                    type: 'success',
                    render: 'Person saved successfully',
                    autoClose: 3000,
                });
            } catch (error) {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving person',
                    autoClose: 5000,
                });
            }
        },
        [reloadPersonnelFromServer, type],
    );

    const removePerson = useCallback(async () => {
        if (!currentPerson) return;

        const toastId = toast.info('Removing person...');

        try {
            await institutionService.removeInstitutionPerson(currentPerson);
            await reloadPersonnelFromServer();
            setSelectedPersonId(null);
            toast.update(toastId, {
                type: 'success',
                render: 'Person removed successfully',
                autoClose: 3000,
            });
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: 'Error removing person',
                autoClose: 5000,
            });
        }
    }, [currentPerson, reloadPersonnelFromServer]);

    const handlePersonSelect = useCallback(
        (personId: number) => {
            if (!editing) {
                setSelectedPersonId(personId);
            }
        },
        [editing],
    );

    const editedByInfo = useMemo(() => {
        if (currentPerson?.people?.editedByString && currentPerson.people.editedByString.indexOf('{') > -1) {
            try {
                return JSON.parse(currentPerson.people.editedByString);
            } catch {
                return undefined;
            }
        }
        return undefined;
    }, [currentPerson]);

    const hasAdminPermission = useMemo(() => authService.hasPermission(AmsPermissionClaimType, 'systemadmin'), []);

    return (
        <>
            <Form noValidate className="flex-grow-1 d-flex" onSubmit={handleSubmit(submitForm)}>
                <Card className="align-self-stretch flex-grow-1">
                    {rolePeople.length > 1 && (
                        <CardHeader className="bg-warning d-flex justify-content-between">
                            <div>
                                <h4 className="mb-0">{InstitutionPersonRole[type]}</h4>
                            </div>
                            <div className="d-flex flex-end align-items-center">
                                {rolePeople.map((rp) => (
                                    <Button
                                        key={rp.institutionPeopleId}
                                        type="button"
                                        className="btn-sm ml-1"
                                        onClick={() => handlePersonSelect(rp.institutionPeopleId)}
                                        disabled={editing}
                                        color={
                                            currentPerson?.institutionPeopleId === rp.institutionPeopleId
                                                ? 'primary'
                                                : 'secondary'
                                        }
                                    >
                                        <span style={{ fontSize: '11px' }}>
                                            {`${rp.people?.firstName} ${rp.people?.lastName}`}
                                        </span>
                                    </Button>
                                ))}
                            </div>
                        </CardHeader>
                    )}
                    <CardBody>
                        {rolePeople.length <= 1 && (
                            <h4 className="card-title d-flex align-items-center justify-content-between">
                                <span>{InstitutionPersonRole[type]}</span>
                            </h4>
                        )}

                        {/* Hidden fields */}
                        <Controller
                            name="institutionPeopleId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="institutionId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="programId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="peopleId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="roleId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />

                        <Row>
                            <Col>
                                <FormGroup className="row">
                                    <Col className="d-flex flex-column">
                                        <Label>
                                            Email
                                            {isLookingUp && <Spinner size="sm" className="ms-2" />}
                                        </Label>
                                        {editing ? (
                                            <Controller
                                                name="people.email"
                                                control={control}
                                                rules={{
                                                    required:
                                                        type === InstitutionPersonRole['Program Director']
                                                            ? 'Email is required for Program Director'
                                                            : false,
                                                }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input
                                                            type="email"
                                                            {...field}
                                                            value={field.value || ''}
                                                            invalid={!!errors.people?.email}
                                                            onBlur={(e) => {
                                                                field.onBlur();
                                                                handleEmailBlur(e.target.value);
                                                            }}
                                                        />
                                                        {errors.people?.email && (
                                                            <span className="text-danger">
                                                                {errors.people.email.message}
                                                            </span>
                                                        )}
                                                        {emailLookupMessage && (
                                                            <span className="text-success small">
                                                                {emailLookupMessage}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.email || ''}</span>
                                        )}
                                    </Col>
                                    <Col className="d-flex flex-column">
                                        <Label>Job Title</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.title"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" disabled={!isEmailEntered} {...field} value={field.value || ''} />
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.title || ''}</span>
                                        )}
                                    </Col>
                                </FormGroup>

                                <FormGroup className="row">
                                    <Col className="d-flex flex-column">
                                        <Label>Salutations</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.salutations"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" disabled={!isEmailEntered} {...field} value={field.value || ''} />
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.salutations || ''}</span>
                                        )}
                                    </Col>
                                    <Col className="d-flex flex-column">
                                        <Label>Credentials</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.credentials"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" disabled={!isEmailEntered} {...field} value={field.value || ''} />
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.credentials || ''}</span>
                                        )}
                                    </Col>
                                </FormGroup>

                                <FormGroup className="row">
                                    <Col className="d-flex flex-column">
                                        <Label>First Name</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.firstName"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" disabled={!isEmailEntered} {...field} value={field.value || ''} />
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.firstName || ''}</span>
                                        )}
                                    </Col>
                                    <Col className="d-flex flex-column">
                                        <Label>Last Name</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.lastName"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" disabled={!isEmailEntered} {...field} value={field.value || ''} />
                                                )}
                                            />
                                        ) : (
                                            <span>{currentPerson?.people?.lastName || ''}</span>
                                        )}
                                    </Col>
                                </FormGroup>

                                <FormGroup className="row">
                                    <Col className="d-flex flex-column">
                                        <Label>Phone</Label>
                                        {editing ? (
                                            <Controller
                                                name="people.phone"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" disabled={!isEmailEntered} {...field} value={field.value || ''} />
                                                )}
                                            />
                                        ) : (
                                            <div>
                                                {formatPhoneNumber(currentPerson?.people?.phone)}{' '}
                                                {currentPerson?.people?.extension
                                                    ? `ext ${currentPerson.people.extension}`
                                                    : ''}
                                            </div>
                                        )}
                                    </Col>
                                    {editing && (
                                        <Col className="d-flex flex-column">
                                            <Label>Extension</Label>
                                            <Controller
                                                name="people.extension"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="text" disabled={!isEmailEntered} {...field} value={field.value || ''} />
                                                )}
                                            />
                                        </Col>
                                    )}
                                </FormGroup>
                            </Col>
                        </Row>

                        {currentPerson &&
                            (currentPerson.peopleId || 0) > 0 &&
                            (currentPerson.people?.lastEdited || editedByInfo) && (
                                <Row>
                                    <Col className="font-italic">
                                        <span>Last edited</span>
                                        {editedByInfo && <span> by {editedByInfo.name}</span>}
                                        {currentPerson.people?.lastEdited && (
                                            <span>
                                                {` on ${moment
                                                    .utc(currentPerson.people.lastEdited)
                                                    .local()
                                                    .format('MM/DD/YYYY [at] hh:mm A')}`}
                                            </span>
                                        )}
                                    </Col>
                                </Row>
                            )}
                    </CardBody>

                    {hasAdminPermission && (
                        <CardFooter className="bg-white border-0 p-0">
                            <ButtonGroup className="w-100">
                                {!editing && (
                                    <>
                                        {currentPerson && currentPerson.institutionPeopleId > 0 && (
                                            <>
                                                <Button color="info" type="button" onClick={() => setEditing(true)}>
                                                    <i className="mdi mdi-pencil" />
                                                    <span className="ml-1">Edit Person</span>
                                                </Button>
                                                <Button color="danger" type="button" onClick={toggleModal}>
                                                    <i className="mdi mdi-delete" />
                                                    <span className="ml-1">Remove Person</span>
                                                </Button>
                                            </>
                                        )}
                                        {currentPerson && currentPerson.institutionPeopleId === 0 && (
                                            <Button color="info" type="button" onClick={() => setEditing(true)}>
                                                <i className="mdi mdi-account-plus" />
                                                <span className="ml-1">Add Person</span>
                                            </Button>
                                        )}
                                    </>
                                )}
                                {editing && (
                                    <>
                                        <Button color="success" type="submit">
                                            <i className="mdi mdi-content-save" />
                                            <span className="ml-1">Submit</span>
                                        </Button>
                                        <Button color="white" type="button" onClick={cancelForm}>
                                            <span className="ml-1">Cancel</span>
                                        </Button>
                                    </>
                                )}
                            </ButtonGroup>
                        </CardFooter>
                    )}
                </Card>
            </Form>

            {currentPerson && currentPerson.people && currentPerson.people.peopleId > 0 && (
                <Modal isOpen={showModal} toggle={toggleModal} centered>
                    <ModalHeader>
                        {`Confirm Remove ${currentPerson.people.firstName} ${currentPerson.people.lastName} (${
                            InstitutionPersonRole[type]
                        })`}
                    </ModalHeader>
                    <ModalBody>
                        <p>
                            {`You have selected to remove ${currentPerson.people.firstName} ${
                                currentPerson.people.lastName
                            } from the role of ${InstitutionPersonRole[type]} at ${
                                institution.title
                            }. To continue, please click the Confirm button below.`}
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <div className="d-flex justify-content-between flex-grow-1">
                            <Button color="primary" onClick={removePerson} type="button">
                                Confirm
                            </Button>
                            <Button color="secondary" onClick={toggleModal} type="button">
                                Cancel
                            </Button>
                        </div>
                    </ModalFooter>
                </Modal>
            )}
        </>
    );
};

export default InstitutionPersonnel;
